import { endpoint } from '@shared/api/config';
import { ApiError } from '@shared/api/envelope';
import { httpRequest } from '@shared/api/http';

import { loadCapabilities } from './capabilities';
import { toNextParam } from './next-target';
import { useSessionStore, type AccountRole, type SessionProfile } from './session';

/**
 * Session lifecycle: refresh, bootstrap, adopt, sign-out.
 *
 * Silent refresh fires **exactly once per failing request** behind a single
 * in-flight `Promise` mutex — N parallel `401`s coalesce into one
 * `POST /api/v1/tokens:refresh`. The request carries no body field: the
 * host-only HttpOnly cookie is the credential (PRD-TDD §11.1).
 *
 * Rotation is single-use server-side, so a stolen refresh token is usable at
 * most once and its use kills the legitimate session — which is the detection
 * signal, not a bug to smooth over.
 *
 * **A refresh returns a token and nothing else.**
 * `ModelRefreshTokensOutputForPublic` is `{access_token, expires_in}`: no
 * profile, no role, no registry. Identity comes from `GET /api/v1/me` on the
 * token the refresh just set. Treating the refresh response as a session
 * payload would leave a cold tab with a live token, a `null` role, and a guard
 * that bounces a signed-in operator to the sign-in screen.
 */

/** `ModelProfileHeadForClient` — snake_case on the wire, mapped once, here. */
interface ProfileHeadPayload {
  id: string;
  display_name: string;
  handle: string;
  email?: string;
  biography?: string;
  avatar_url?: string;
}

/** `ModelRefreshTokensOutputForPublic`. */
interface RefreshPayload {
  access_token: string;
  expires_in: number;
}

/** `ModelGetMeOutputForClient`. */
interface MePayload {
  profile: ProfileHeadPayload;
  role_name: string;
  admin_roles: Record<string, string>;
  is_blocked: boolean;
}

/** `ModelCreateSessionOutputForPublic` — the one response that carries both. */
export interface CreateSessionPayload {
  access_token: string;
  expires_in: number;
  profile: ProfileHeadPayload;
  role_name: string;
  admin_roles: Record<string, string>;
}

/** Codes that end the session outright rather than inviting another attempt. */
const TERMINAL_AUTH_CODES = new Set(['AUTH_REFRESH_REPLAYED', 'AUTH_SESSION_REVOKED']);

const ACCOUNT_ROLES: readonly AccountRole[] = ['user', 'operator', 'admin'];

let inFlightRefresh: Promise<string> | null = null;

export function isTerminalAuthError(error: unknown): boolean {
  return error instanceof ApiError && TERMINAL_AUTH_CODES.has(error.code);
}

/**
 * `role_name` is a string on the wire because the service reads it out of
 * `auth__roles`, whose rows are seeded from code rather than compiled into a
 * client. A value this bundle does not know is **not** guessed at: it resolves
 * to `user`, the role with the fewest rights, so an unrecognised grant closes
 * the console rather than opening it.
 */
function toAccountRole(roleName: string): AccountRole {
  return ACCOUNT_ROLES.includes(roleName as AccountRole) ? (roleName as AccountRole) : 'user';
}

export function toSessionProfile(payload: ProfileHeadPayload): SessionProfile {
  return {
    id: payload.id,
    displayName: payload.display_name,
    handle: payload.handle,
    // Spread rather than assign: the wire omits an absent optional, and a key
    // written as `undefined` is a key the surface has to test for twice.
    ...(payload.email !== undefined ? { email: payload.email } : {}),
    ...(payload.biography !== undefined ? { biography: payload.biography } : {}),
    ...(payload.avatar_url !== undefined ? { avatarUrl: payload.avatar_url } : {}),
  };
}

/**
 * Resolves with a fresh access token, or throws. Concurrent callers share one
 * network call.
 */
export function refreshOnce(): Promise<string> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = httpRequest<RefreshPayload>(endpoint('auth', '/tokens:refresh'), { method: 'POST' })
    .then((payload) => {
      useSessionStore.getState().setAccessToken(payload.access_token);
      return payload.access_token;
    })
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}

/**
 * Reads the caller's own account onto the token already in the slice.
 *
 * It goes through `httpRequest` with an explicit header rather than through
 * `@shared/api/client`, because that wrapper retries a `401` behind this very
 * mutex — and the one caller here has just refreshed. A second refresh inside
 * the bootstrap would rotate a token nothing had used yet.
 */
export async function loadMe(): Promise<void> {
  const { accessToken } = useSessionStore.getState();

  const payload = await httpRequest<MePayload>(endpoint('auth', '/me'), {
    method: 'GET',
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });

  useSessionStore.getState().setIdentity({
    profile: toSessionProfile(payload.profile),
    role: toAccountRole(payload.role_name),
    adminRoles: payload.admin_roles,
  });
}

/**
 * Reads what the caller may do in each of the nine services onto the slice.
 *
 * **Never throws, and never blocks a session on its own failure.** A signed-in
 * operator with nine unreachable RBAC endpoints still gets a console; every
 * service is simply unknown, which the capability map treats as visible and the
 * backend refuses on arrival exactly as it did before this existed.
 *
 * It runs *after* `loadMe()` rather than beside it, because it needs the token
 * that call proved good — nine `401`s racing the same refresh mutex is a worse
 * failure than one.
 */
export async function loadCapabilityMap(): Promise<void> {
  const { accessToken } = useSessionStore.getState();
  try {
    useSessionStore.getState().setCapabilities(await loadCapabilities(accessToken));
  } catch {
    // `loadCapabilities` resolves rather than rejects, so this is the second
    // belt. The flag is still set: every service stays unknown, which renders
    // the full navigation, and a failed sweep must not become nine requests on
    // every navigation afterwards.
    useSessionStore.getState().setCapabilities({});
  }
}

/**
 * Takes a completed sign-in. `POST /api/v1/sessions` is the **only** response
 * on this service that carries the token and the identity together, so this is
 * the one path that needs no `/me` round trip.
 */
export function adoptSession(payload: CreateSessionPayload): void {
  useSessionStore.getState().setSession({
    profile: toSessionProfile(payload.profile),
    role: toAccountRole(payload.role_name),
    adminRoles: payload.admin_roles,
    accessToken: payload.access_token,
  });
}

/**
 * Ends a session that died **between** navigations, and sends the tab to sign in.
 *
 * The guard already covers the navigation case; nothing covered this one. A read
 * that fires while the page is already open — a refetch, an invalidation, a poll —
 * runs no loader, so a session that ends underneath it surfaced as every panel on
 * the page printing «انتهت الجلسة. سجّل الدخول من جديد» beside a *Retry* button
 * that could never succeed. The operator was told to sign in again and given no way
 * to do it.
 *
 * **The common way to get here is two tabs, not an expiry.** The refresh token
 * rotates single-use, and `refreshOnce()`'s mutex is per-tab: a second console tab
 * refreshing first leaves this one presenting a token that has already been spent,
 * which `backend-auth` answers `AUTH_REFRESH_REPLAYED` — deliberately, because a
 * replay is how a stolen token announces itself. It is a real end of session, so it
 * is treated as one rather than smoothed over.
 *
 * A full document navigation rather than a router redirect, because this module sits
 * under the router and has no history handle — and because nothing in the tab is
 * worth preserving once the session behind it is gone.
 */
export function endSession(): void {
  useSessionStore.getState().clear();

  if (typeof window === 'undefined') return;

  const { pathname, search } = window.location;
  // Already on the sign-in screen: a second navigation would loop, and would
  // overwrite a `next` the guard set with the sign-in page's own address.
  if (pathname.startsWith('/login')) return;

  try {
    window.location.assign(`/login?next=${toNextParam({ pathname, search })}&reason=session`);
  } catch {
    // jsdom implements no navigation and throws here. The session is cleared
    // either way, which is the half that has to hold under test; the navigation
    // is exercised against the running console instead.
  }
}

/**
 * Establishes the session for a cold tab: the access token is memory-only, so
 * a reload always starts here. Never throws — a failure is "no session", which
 * the guard turns into a redirect.
 */
export async function bootstrapSession(): Promise<boolean> {
  const store = useSessionStore.getState();

  if (store.accessToken && store.profile) {
    // **A sign-in takes this branch.** `POST /api/v1/sessions` carries the token
    // and the identity together, so `adoptSession` fills the slice and the first
    // guard run finds a warm session and returns — which meant the capability
    // sweep never ran for anyone who had just signed in, and the console drew
    // every destination until the tab was reloaded. The flag, not the map, is
    // what makes this ask once: an empty map is also what nine failed reads
    // produce, and re-sweeping on that would put nine requests on every
    // navigation.
    if (!store.capabilitiesLoaded) await loadCapabilityMap();
    return true;
  }

  if (import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
    seedDevSession();
    return true;
  }

  try {
    await refreshOnce();
    await loadMe();
  } catch {
    useSessionStore.getState().clear();
    return false;
  }

  // Outside the `try` on purpose. A session is established by the two calls
  // above; the capability read shapes the console and cannot un-establish one.
  // `loadCapabilityMap` swallows its own failures, and this catch is the second
  // belt — an unexpected throw here must not sign an operator out.
  try {
    await loadCapabilityMap();
  } catch {
    // Every service stays unknown, which renders the full navigation and lets
    // the owning backend answer — the behaviour that shipped before this.
  }
  return true;
}

/**
 * Clears the in-memory slice **regardless of the response**, so a failed
 * revoke never leaves a live access token in the tab.
 */
export async function signOut(): Promise<void> {
  const { accessToken } = useSessionStore.getState();

  try {
    await httpRequest<{ revoked: boolean }>(endpoint('auth', '/sessions:revoke'), {
      method: 'POST',
      ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
    });
  } catch {
    // Deliberately swallowed — see above.
  } finally {
    useSessionStore.getState().clear();
  }
}

/**
 * DEV-only synthetic session, so the local dev server renders authenticated
 * surfaces against MSW with no backend running. The guard reads
 * `import.meta.env.DEV`, which Vite replaces at compile time, so this branch is
 * dead code in every production build and a CI grep asserts the flag name does
 * not appear in the emitted `dist/` (PRD-TDD §11.4).
 */
function seedDevSession(): void {
  useSessionStore.getState().setCapabilities({
    auth: 'admin',
    content: 'admin',
    assets: 'admin',
    finance: 'admin',
    support: 'admin',
    engagement: 'admin',
    analytics: 'admin',
    notification: 'admin',
    search: 'admin',
  });
  useSessionStore.getState().setSession({
    profile: {
      id: '01JDEVBYPASS0000000000000',
      displayName: 'مشغّل التطوير',
      handle: 'dev-operator',
    },
    role: 'admin',
    adminRoles: {},
    accessToken: 'dev-bypass-token',
  });
}
